import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { BUTTON_VARIANTS } from '../../../../constants/button-variants';
import Routes from '../../../../constants/routes';
import useIsMobile from '../../../../hooks/use-is-mobile';
import { ReactComponent as Cart } from '../../../../images/icon-warenkorb_.svg';
import translation from '../../../../languages';
import { reset } from '../../../../reducers/cart';
import { changeFavorite } from '../../../../reducers/favorite';
import cartService from '../../../../services/cart-service';
import foldAnimation from '../../../../utils/animations/fold';
import enhancedEcommerce from '../../../../utils/enhanced-ecommerce';
import formatPrice from '../../../../utils/price/format-price';
import Session from '../../../../utils/session';
import makeUrl from '../../../../utils/url/make-url';
import Button from '../../../button/button';
import CurrencyPrice from '../../../currency-price/currency-price';
import DomainSensitiveLink from '../../../domain-sensitive-link/domain-sensitive-link';
import FontAwesomeIcon from '../../../font-awesome-icon/font-awesome-icon';
import Preview from '../../../preview/preview';
import './slide-down.scss';

const onMaterialsClick = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
};

const maxLen = 10;

type SlideDownProps = RouterWithTranslation & {
    shouldShow: boolean;
    temporaryShow?: boolean;
};

const SlideDown: FC<SlideDownProps> = (props) => {
    const { t, temporaryShow, shouldShow, history } = props;

    const dispatch = useDispatch();
    const cart = useSelector((state: RootState) => state.main.cart);
    const lastItem = cart.cartItems[0];

    const ref = useRef<HTMLDivElement>(null);
    const isFocused = useRef(false);
    const isAnimating = useRef(false);

    const [showSlide, setShow] = useState(false);

    const mobile = useIsMobile();
    const top = mobile ? '0px' : '60px';

    const goToCart = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = `${Routes.WARENKORB.path}`;
    };

    const goToPayment = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        history.push(`${Routes.PAYMENT.path}`);
    };

    useEffect(() => {
        isAnimating.current = true;

        if (showSlide) {
            if (ref.current) {
                ref.current.style.top = top;
            }
        } else if (ref.current) {
            ref.current.style.top = '-600px';
        }

        setTimeout(() => {
            isAnimating.current = false;
        }, 700);
    }, [showSlide]);

    useEffect(() => {
        return () => {
            if (ref.current) {
                ref.current.style.top = '-600px';
            }
        };
    }, []);

    const removeItem = (item: CartItem) => {
        foldAnimation(`.cart-item-${item.material.id}`, () => {
            cartService.remove({}, { id: item.material.id }).then(
                ({
                    data: {
                        items: { cart }
                    }
                }) => {
                    console.info(cart);

                    dispatch(reset(cart));
                }
            );
        });

        enhancedEcommerce.tracker.removeFromCart(item.material);
    };

    useEffect(() => {
        if (temporaryShow) {
            setShow(true);
        } else if (showSlide && !isFocused.current) {
            setShow(false);
        }
    }, [temporaryShow]);

    useEffect(() => {
        if (shouldShow && !showSlide && isAnimating.current) {
            return;
        }

        setShow(shouldShow);
    }, [shouldShow]);

    const moveToFavorites = (item: CartItem) => {
        dispatch(changeFavorite(item.material, true));
        removeItem(item);
    };

    if (!lastItem) {
        return null;
    }

    const items = cart?.cartItems?.slice(0, maxLen);

    const warenkorbContent = () => {
        const empty = cart.cartItems.length ? '' : 'empty';

        return (
            <div className='align-items-center d-flex justify-content-between flex-column w-link'>
                <div className={`icon-wrapper position-relative ${empty}`} data-amount={cart.cartItems.length}>
                    <Cart className='w-icon' />
                </div>
            </div>
        );
    };

    if (mobile) {
        return (
            <div ref={ref} className='d-flex flex-column slide-down cart-test slide-down-mobile'>
                <div className='mt-3 w-100 position-relative'>
                    <div className='warenkorb-menu d-flex align-items-center justify-content-center'>
                        {warenkorbContent()}
                    </div>
                </div>
                <span className='tsc-body-highlighted text-center'>
                    {t('cart.cartSummary.originalPrice')}:{' '}
                    <span>
                        <CurrencyPrice price={formatPrice(cart.finalPrice)} />
                    </span>
                </span>
                <Button className='btns' variant={BUTTON_VARIANTS.YELLOW} onClick={(e) => goToCart(e as never)}>
                    {t('modals:modals.addToCartModal.goToCart')}
                </Button>
            </div>
        );
    }

    return (
        <div onClick={(e) => goToCart(e as never)} ref={ref} className='slide-down cart-test'>
            <div className='tsc-h4 mb-24 d-flex align-items-center'>
                <Cart className='cart-img mr-2' />
                {t('cart.cartItems.cart')}
            </div>
            {temporaryShow && (
                <div className='tsc-body mb-24 info-block d-flex justify-content-center align-items-center w-100'>
                    <FontAwesomeIcon className='mr-2' name='check_circle' />
                    {t('cart.cartSummary.info')}
                </div>
            )}
            <div className='items-block' onClick={(e) => onMaterialsClick(e as never)}>
                {items.map((cartItem: CartItem) => {
                    return (
                        <div
                            key={cartItem.material.id}
                            className={`cart-item-${cartItem.material.id} d-flex justify-content-between cart-item mb-24`}
                        >
                            <div className='left-container d-flex w-100'>
                                <DomainSensitiveLink
                                    href={makeUrl(Routes.MATERIAL.pathByWorld(cartItem.material.world), {
                                        id: cartItem.material.id,
                                        slug: cartItem.material.slug
                                    })}
                                >
                                    <Preview height={112} item={cartItem.material} width={78} />
                                </DomainSensitiveLink>

                                <div className='ml-3'>
                                    <DomainSensitiveLink
                                        className='no-styles-link'
                                        href={makeUrl(Routes.MATERIAL.pathByWorld(cartItem.material.world), {
                                            id: cartItem.material.id,
                                            slug: cartItem.material.slug
                                        })}
                                    >
                                        <div className='tsc-body'>{cartItem.material.title?.slice(0, 52)}</div>
                                    </DomainSensitiveLink>

                                    <div className='mt-24 secondary-gray'>
                                        {Session.isLoggedIn() && (
                                            <span className='cursor-pointer' onClick={() => moveToFavorites(cartItem)}>
                                                {t('common:common.materialBlock.addToFavorites')} |{' '}
                                            </span>
                                        )}
                                        <span className='cursor-pointer' onClick={() => removeItem(cartItem)}>
                                            {t('cart.cartItems.remove')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className='price-section tsc-body-highlighted '>
                                <CurrencyPrice price={formatPrice(cartItem.finalPrice)} />
                            </div>
                        </div>
                    );
                })}
            </div>
            {cart.cartItems.length > maxLen && (
                <div className='econdary-gray mt-24'>
                    {t('cart.cartSummary.moreItems', { total: cart.cartItems.length - maxLen })}
                </div>
            )}
            <hr />
            <div className='d-flex justify-content-between align-items-center mb-24 tsc-h4'>
                <span>
                    {t('cart.cartSummary.sum')}{' '}
                    <span className='tsc-body secondary-gray'>({t('cart.cartSummary.includesTax')})</span>
                </span>
                <span>
                    <CurrencyPrice price={formatPrice(cart.finalPrice)} />
                </span>
            </div>
            <div className='d-flex justify-content-between'>
                <Button className='btns' variant={BUTTON_VARIANTS.TRANSPARENT} onClick={(e) => goToCart(e as never)}>
                    {t('modals:modals.addToCartModal.goToCart')}
                </Button>
                <Button className='btns' variant={BUTTON_VARIANTS.YELLOW} onClick={(e) => goToPayment(e as never)}>
                    {t('cart.cartSummary.goToPayment')}
                </Button>
            </div>
        </div>
    );
};

const translate = translation(['cart', 'modals']);

export default withRouter(translate(SlideDown));
